import React, { useEffect, useRef, useState } from 'react';
import { css } from 'glamor';
import { useMutation, useQuery } from '@apollo/client';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import authManager, { DEFAULT_ROLE_PERMISSIONS, PERMISSIONS, ROOT_ONLY_PERMISSIONS } from "../../../lib/authManager";
import { createRoleMutation, deleteRoleMutation, roleQuery, updateRoleMutation } from "../Users/queries";
import { Container, CardContainer, ColContainer, TPL_50_50 } from "../../../components/Container";
import {Loading, LoadingError} from "../../../components/LoadingFork";
import {ValidationErrorMessage} from "../../../components/ErrorMessage";
import {Alert} from "@material-ui/lab";
import {H2} from "../../../components/Typography";
import {CheckboxInput} from "../../../components/CheckboxInput";
import {Button, HollowButton} from "../../../components/Button";
import {Input} from '../../../components/Input';
import {withInstallation} from "../../../components/InstallationContext";

/**
 * Pull the real reason out of an Apollo error so the form can show it.
 * The API prefixes user-safe messages with `SafeError`; anything else is
 * unexpected, so fall back to a generic line rather than leaking internals.
 */
export function toRoleErrorMessage(error, fallback) {
    const raw =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.result?.errors?.[0]?.message ||
        error?.message ||
        '';
    const cleaned = raw.replace('GraphQL error: ', '');

    if (cleaned.startsWith('SafeError')) {
        return cleaned.replace(/^SafeError\s*/, '');
    }

    return fallback;
}

export function formatPermissionLabel(permission) {
    return permission
        .split(':')
        .map((part) => part.replace(/_/g, ' '))
        .join(' / ');
}

function RoleDetails({ match, history, installation }) {
    const { roleId } = match.params;
    const isEditMode = Boolean(roleId === 'new' ? null : roleId);

    const [name, setName] = useState('');
    const [label, setLabel] = useState('');
    const [description, setDescription] = useState('');
    const [selectedPermissions, setSelectedPermissions] = useState(
        DEFAULT_ROLE_PERMISSIONS
    );

    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    /**
     * The success/error banners sit at the top of the form while Save sits
     * below the permissions list, so scroll them into view after a submit —
     * imperatively (next frame, once the banner renders) so it fires every
     * time, even when the same message repeats.
     */
    const bannerAnchorRef = useRef(null);
    const scrollToBanner = () => {
        requestAnimationFrame(() => {
            if (bannerAnchorRef.current) {
                bannerAnchorRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                });
            }
        });
    };
    const flagError = (message) => {
        setShowSuccessMessage(false);
        setErrorMessage(message);
        scrollToBanner();
    };
    const flagSuccess = () => {
        setErrorMessage(null);
        setShowSuccessMessage(true);
        scrollToBanner();
    };

    const {
        data = {},
        loading,
        error,
    } = useQuery(roleQuery, {
        fetchPolicy: 'no-cache',
        skip: !isEditMode,
        variables: {
            id: roleId,
        },
    });

    const [createRole, { loading: creating }] = useMutation(createRoleMutation);
    const [updateRole, { loading: updating }] = useMutation(updateRoleMutation);
    const [deleteRole, { loading: deleting }] = useMutation(deleteRoleMutation);

    const role = data.role;
    const saving = creating || updating;

    useEffect(() => {
        if (!isEditMode) {
            setSelectedPermissions(DEFAULT_ROLE_PERMISSIONS);
            return;
        }

        if (!role) return;

        setName(role.name || '');
        setLabel(role.label || '');
        setDescription(role.description || '');
        setSelectedPermissions(
            (role.permissions || []).map(({ permission }) => permission)
        );
    }, [isEditMode, role]);

    const isRootAdmin = Boolean(authManager.isRootAdmin());
    const isGlobalRole = isEditMode && role && !role.installationId;

    /**
     * Root-only permissions can't be granted by non-root users, and never on a
     * global role (the API rejects both) — lock the checkbox instead.
     */
    const isPermissionLocked = (permission) => {
        if (!ROOT_ONLY_PERMISSIONS.includes(permission)) return false;
        return !isRootAdmin || isGlobalRole;
    };

    /**
     * A locked permission the role already carries stays on screen — greyed out
     * and ticked — so the form still shows what the role can do. It is only
     * hidden when the role doesn't have it, where showing it would just be an
     * unusable checkbox.
     */
    const visiblePermissions = Object.values(PERMISSIONS).filter(
        (permission) =>
            !isPermissionLocked(permission) ||
            selectedPermissions.includes(permission)
    );

    const togglePermission = (permission) => {
        if (isPermissionLocked(permission)) return;

        if (selectedPermissions.includes(permission)) {
            setSelectedPermissions(
                selectedPermissions.filter((item) => item !== permission)
            );
            return;
        }

        setSelectedPermissions([...selectedPermissions, permission]);
    };

    const validateForm = () => {
        return name.trim() && label.trim();
    };

    const handleSubmit = async () => {
        setShowSuccessMessage(false);
        setErrorMessage(null);

        if (!validateForm()) {
            flagError('Please fill in all required fields and try again.');
            return;
        }

        try {
            const input = {
                name: name.trim(),
                label: label.trim(),
                description: description || null,
                permissions: selectedPermissions,
            };

            if (isEditMode) {
                await updateRole({
                    variables: {
                        input: {
                            id: roleId,
                            ...input,
                        },
                    },
                });

                flagSuccess();
                return;
            }

            const { data } = await createRole({
                variables: {
                    input: {
                        ...input,
                        installationId: installation.id,
                    },
                },
            });

            const createdRoleId = data?.createRole?.id;

            if (createdRoleId) {
                history.replace(`/${installation.brandingKey}/settings/roles/${createdRoleId}`);
                return;
            }

            history.goBack();
        } catch (error) {
            console.error('Error saving role:', error);
            flagError(
                toRoleErrorMessage(
                    error,
                    'Could not save this role. Please try again.',
                ),
            );
        }
    };

    const handleDelete = async () => {
        if (!isEditMode) return;

        try {
            await deleteRole({
                variables: {
                    id: roleId,
                },
            });

            setDeleteDialogOpen(false);
            history.goBack();
        } catch (error) {
            console.error('Error deleting role:', error);
            setDeleteDialogOpen(false);
            flagError(
                toRoleErrorMessage(
                    error,
                    'Could not delete this role. Please try again.',
                ),
            );
        }
    };

    if (isEditMode && loading) {
        return (
            <CardContainer>
                <Loading />
            </CardContainer>
        );
    }

    if (isEditMode && error) {
        return (
            <CardContainer>
                <LoadingError error={error} />
            </CardContainer>
        );
    }

    if (isEditMode && !role) {
        return (
            <CardContainer>
                <Container spaceAbove="medium" spaceBelow="medium">
                    <ValidationErrorMessage error="Role not found." />
                </Container>
            </CardContainer>
        );
    }

    if (role && (!role.installationId && !authManager.isRootAdmin())) {
        return (
            <CardContainer>
                <Container spaceAbove="medium" spaceBelow="medium">
                    <ValidationErrorMessage error="You are not authorized to edit this role." />
                </Container>
            </CardContainer>
        )
    }

    return (
        <div>
            <CardContainer>
                <Container spaceAbove="medium" spaceBelow="medium">
                    <span ref={bannerAnchorRef} />
                    <H2>{isEditMode ? 'Edit Role' : 'Create Role'}</H2>

                    {showSuccessMessage && (
                        <Alert severity="success">Your changes have been saved.</Alert>
                    )}

                    {errorMessage && (
                        <ValidationErrorMessage
                            spaceBelow="medium"
                            error={errorMessage}
                        />
                    )}

                    <ColContainer columnTemplate={TPL_50_50} spaceAbove="medium">
                        <Input
                            label="Name"
                            name="name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                        />

                        <Input
                            label="Label"
                            name="label"
                            value={label}
                            onChange={(event) => setLabel(event.target.value)}
                        />
                    </ColContainer>

                    <div
                        className={css({
                            marginTop: 20,
                        })}
                    >
                        <Input
                            label="Description"
                            name="description"
                            value={description}
                            onChange={(event) => setDescription(event.target.value)}
                        />
                    </div>

                    <div
                        className={css({
                            marginTop: 30,
                        })}
                    >
                        <H2>Permissions</H2>

                        <div
                            className={css({
                                display: 'grid',
                                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                                gridGap: 12,
                                marginTop: 16,
                            })}
                        >
                            {visiblePermissions.map((permission) => (
                              <label
                                key={permission}
                                className={css({
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 10,
                                    cursor: isPermissionLocked(permission) ? 'not-allowed' : 'pointer',
                                    minHeight: 32,
                                })}
                              >
                                  <CheckboxInput
                                    checked={selectedPermissions.includes(permission)}
                                    disabled={isPermissionLocked(permission)}
                                    onChange={() => togglePermission(permission)}
                                  />

                                  <span
                                    className={css({
                                        fontSize: 14,
                                        lineHeight: '20px',
                                    })}
                                  >
                                          {formatPermissionLabel(permission)}
                                          {isPermissionLocked(permission) && ' (root admin only)'}
                                        </span>
                              </label>
                            ))}
                        </div>
                    </div>

                    <ColContainer spaceBelow="medium">
                        <Button
                            type="submit"
                            style={{ cursor: 'pointer' }}
                            spaceAbove="medium"
                            flavour="primary"
                            onClick={handleSubmit}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : isEditMode ? 'Update Role' : 'Create Role'}
                        </Button>

                        {isEditMode && (
                            <Button
                                block
                                style={{ cursor: 'pointer' }}
                                spaceAbove="medium"
                                flavour="danger"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                Delete
                            </Button>
                        )}
                    </ColContainer>
                </Container>
            </CardContainer>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-role-dialog-title"
                aria-describedby="delete-role-dialog-description"
            >
                <DialogContent>
                    <DialogContentText id="delete-role-dialog-description">
                        Are you sure you want to delete this role?
                    </DialogContentText>
                </DialogContent>

                <DialogActions>
                    <HollowButton size="small" onClick={() => setDeleteDialogOpen(false)}>
                        Cancel
                    </HollowButton>

                    <HollowButton
                        size="small"
                        flavour="danger"
                        onClick={handleDelete}
                        disabled={deleting}
                        autoFocus
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </HollowButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default withInstallation(RoleDetails);
